import { getPositionWithFallback, useLocationPermissionSync } from "../../../../utils/getPositionWithFallback";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, IconButton, useTheme, useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import crossmap from "../../../../assets/crossmap.svg";
import { MAPS_API_KEY } from "../../../../components/mapKey";
import { useGeolocated } from "react-geolocated";
import LocationPermissionModal from "../../../../components/PermissionModal";
import {
  GoogleMap,
  Marker,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";

const circularRadius = 100;
const containerStyle = {
  width: "100%",
  height: "100%",
};

const Location = ({
  onChange,
  onPermission,
  externalCenter,
  initialAddress,
  hideAddressOnEdit,
}) => {
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState(
    hideAddressOnEdit ? "" : initialAddress || "No address"
  );
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showFullMap, setShowFullMap] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pinMoved, setPinMoved] = useState(false);
const [hasCheckedPermission, setHasCheckedPermission] = useState(false);



// Add this useEffect to check permission when component mounts
useEffect(() => {
  if (!hasCheckedPermission) {
    setHasCheckedPermission(true);
    
    // Check if geolocation permission is granted on mount
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied') {
          setShowPermissionModal(true);
          onPermission?.(false);
        } else if (result.state === 'granted') {
          handleGetCurrentLocation();
        } else {
          // 'prompt' state - will ask when user clicks room icon
          handleGetCurrentLocation();
        }
      });
    } else {
      // Fallback for browsers that don't support permissions API
      handleGetCurrentLocation();
    }
  }
}, [hasCheckedPermission]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const addressRef = useRef(null);

  // Helper function to check internet connectivity
  const isOnline = () => navigator.onLine;

  // Enable the close ("X") button on the native Street View panorama that
  // opens when the map's built-in pegman is dragged onto the map, so users
  // can get back to the map view (it's off by default in the JS API).
  const handleMapLoad = (map) => {
    const panorama = map.getStreetView();
    panorama.setOptions({
      enableCloseButton: true,
      // Restrict the pegman to official outdoor Street View coverage only.
      // Without this it can snap to low-quality/broken user- or business-
      // contributed 360 photos (indoor photospheres), which sometimes render
      // solid black.
      source: window.google.maps.StreetViewSource.OUTDOOR,
    });

    // Force a resize whenever the panorama becomes visible or the shown
    // pano changes (e.g. moving along the road) - its tiles can otherwise
    // render solid black inside a React-managed container.
    const triggerResize = () => {
      requestAnimationFrame(() => {
        window.google.maps.event.trigger(panorama, "resize");
      });
      setTimeout(() => {
        window.google.maps.event.trigger(panorama, "resize");
      }, 300);
    };

    panorama.addListener("visible_changed", () => {
      if (panorama.getVisible()) triggerResize();
    });
    panorama.addListener("pano_changed", () => {
      if (panorama.getVisible()) triggerResize();
    });
  };

  const { coords, isGeolocationEnabled } = useGeolocated({
    positionOptions: { enableHighAccuracy: true },
    userDecisionTimeout: 5000,
  });

  /* ---------- Geolocation ---------- */
  useEffect(() => {
    if (coords) {
      const newPos = { lat: coords.latitude, lng: coords.longitude };
      setPosition(newPos);
      fetchAddress(newPos);
      onPermission?.(true);
    } else if (!isGeolocationEnabled && externalCenter) {
      setPosition(externalCenter);
      fetchAddress(externalCenter);
      onPermission?.(false);
    }
  }, [coords, isGeolocationEnabled, externalCenter]);

  /* ---------- Fetch Address ---------- */
  const fetchAddress = ({ lat, lng }) => {
    // Check internet before fetching
    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      setAddress("No internet connection");
      if (addressRef.current) {
        addressRef.current.value = "No internet connection";
      }
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const formattedAddress = results[0].formatted_address;
        
        console.log("🗺️ Reverse Geocoding Result:", results[0]);

        // Extract address components
        const addressComponents = results[0].address_components;
        
        let country = "";
        let province = "";
        let city = "";
        let community = "";
        let postalCode = "";
        let streetAddress = "";
        let region = "";

        // Parse address components
        addressComponents.forEach((component) => {
          const types = component.types;
          
          if (types.includes("country")) {
            country = component.long_name;
          }
          
          if (types.includes("administrative_area_level_1")) {
            province = component.long_name; // State/Province
          }
          
          if (types.includes("locality") || types.includes("postal_town")) {
            city = component.long_name;
          }
          
          if (types.includes("sublocality") || types.includes("neighborhood")) {
            community = component.long_name;
          }
          
          if (types.includes("postal_code")) {
            postalCode = component.long_name;
          }
          
          if (types.includes("route")) {
            streetAddress = component.long_name;
          }
          
          if (types.includes("street_number")) {
            streetAddress = component.long_name + " " + streetAddress;
          }
        });

        // If city is not found, try alternative components
        if (!city) {
          addressComponents.forEach((component) => {
            if (component.types.includes("administrative_area_level_2")) {
              city = component.long_name;
            }
          });
        }

        // Set region based on country (simplified logic)
        if (country) {
          region = "North"; // Default region, you can add more logic here
        }

        console.log("📍 Extracted Address Components:", {
          country,
          province,
          city,
          community,
          postalCode,
          streetAddress,
          region,
        });

        setAddress(formattedAddress);
        if (addressRef.current) {
          addressRef.current.value = formattedAddress;
        }

        // ✅ Pass all extracted fields to onChange
        onChange?.({
          latitude: lat,
          longitude: lng,
          address: formattedAddress,
          country,
          province,
          city,
          community,
          postalCode,
          streetAddress,
          region,
        });
      } else {
        console.error("❌ Geocoding failed:", status);
        if (!isOnline()) {
          toast.error(t("locationInternetError") || "No internet connection");
          setAddress("No internet connection");
        } else {
          setAddress("Address not found");
        }
        
        // Still pass lat/lng even if geocoding fails
        onChange?.({
          latitude: lat,
          longitude: lng,
          address: "Address not found",
          country: "",
          province: "",
          city: "",
          community: "",
          postalCode: "",
          streetAddress: "",
          region: "",
        });
      }
    });
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }

    // Check internet before fetching suggestions
    if (!isOnline()) {
      setSuggestions([]);
      return;
    }

    const timeout = setTimeout(() => {
      const service = new window.google.maps.places.AutocompleteService();

      service.getPlacePredictions(
        { input: searchQuery },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleSearchSelect = (sug) => {
    // Check internet before search
    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId: sug.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;

        const newPos = {
          lat: location.lat(),
          lng: location.lng(),
        };

        console.log("🔍 Search Selected Location:", newPos);

        setPosition(newPos);
        setPinMoved(true);
        fetchAddress(newPos); // This will now extract and pass all address fields
        setSearchQuery(results[0].formatted_address);
        setSuggestions([]);
      }
    });
  };

const handleGetCurrentLocation = () => {
  if (!navigator.geolocation) {
    toast.error(t("geolocationNotSupported") || "Geolocation not supported");
    return;
  }

  // Check internet connectivity
  if (!isOnline()) {
    toast.error(t("locationInternetError") || "No internet connection. Please check your connection.");
    return;
  }

  // Use manual geolocation to catch permission errors
  getPositionWithFallback(
    (pos) => {
      const newPos = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      };
      setPosition(newPos);
      fetchAddress(newPos);
      setPinMoved(true);
      onPermission?.(true);
      setShowPermissionModal(false); // ✅ Hide modal on success
    },
    (error) => {
      console.error("Geolocation error:", error);
      
      if (error.code === error.PERMISSION_DENIED) {
        setShowPermissionModal(true); // ✅ Show modal on denial
        onPermission?.(false);
      } else if (error.code === error.TIMEOUT) {
        toast.error(t("locationTimeout") || "Location request timed out");
      } else {
        toast.error(t("unableToFetchLocation") || "Unable to fetch location");
      }
    }
  );
};

  // Online/Offline event listeners

  // Reflect browser location allow/block changes live (no refresh needed).
  useLocationPermissionSync({
    onGranted: () => {
      onPermission?.(true);
      if (showPermissionModal) {
        setShowPermissionModal(false);
        handleGetCurrentLocation();
      }
    },
    onDenied: () => {
      setShowPermissionModal(true);
      onPermission?.(false);
    },
  });
  useEffect(() => {
    const handleOnline = () => {
      toast.success(t("internetRestored") || "Internet connection restored");
      // Retry fetching address if coordinates exist
      if (position?.lat && position?.lng) {
        fetchAddress(position);
      }
    };

    const handleOffline = () => {
      toast.error(t("internetConnectionLost") || "Internet connection lost. Please check your network.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [position]);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <CustomText customStyle={textStyles.body4}>
          {t("pickFromLive")} <span style={{ color: "red" }}>*</span>
        </CustomText>

        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => setSearchBarOpen((prev) => !prev)}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: 1,
              width: 40,
              height: 31,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={handleGetCurrentLocation}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: 1,
              width: 40,
              height: 31,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            <RoomIcon fontSize="small" />
          </IconButton>

          <CustomButton
            label={t("fullMap")}
            size="small"
            onClick={() => setShowFullMap(true)}
          />
        </Box>
      </Box>

      {/* Search Bar */}
      {searchBarOpen && (
        <Box sx={{ mb: 2, position: "relative" }}>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("searchLocation")}
            style={{
              width: "100%",
              padding: "10px 14px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              fontFamily: "Poppins, sans-serif",
            }}
          />

          {suggestions.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                zIndex: 1000,
                background: "#fff",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 2,
              }}
            >
              {suggestions.map((sug, idx) => (
                <Box
                  key={idx}
                  sx={{ p: 1, cursor: "pointer" }}
                  onClick={() => handleSearchSelect(sug)}
                >
                  {sug.description}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Map */}
      <Box sx={{ height: 300, borderRadius: 2, overflow: "hidden" }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={position || { lat: 37.0902, lng: -95.7129 }}
          zoom={15}
          onLoad={handleMapLoad}
        >
          {position && (
            <>
              <Marker
                position={position}
                draggable
                onDragEnd={(e) => {
                  const newPos = {
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                  };
                  setPosition(newPos);
                  fetchAddress(newPos);
                }}
              />

              <Circle
                center={position}
                radius={circularRadius}
                options={{
                  fillColor: "#3f51b5",
                  fillOpacity: 0.2,
                  strokeColor: "#3f51b5",
                }}
              />
            </>
          )}
        </GoogleMap>
      </Box>

      {/* Address */}
      <textarea
        ref={addressRef}
        readOnly
        value={address}
        style={{
          width: "100%",
          marginTop: 10,
          padding: 12,
          borderRadius: 8,
          border: "1px solid #ccc",
          backgroundColor: "#f4f4f4",
          fontFamily: 'Poppins, sans-serif',
        }}
      />

      {/* Full Map */}
      {showFullMap && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 1300,
            background: "#fff",
          }}
        >
          <IconButton
            onClick={() => setShowFullMap(false)}
            sx={{ position: "absolute", top: 16, right: 16 }}
          >
            <img src={crossmap} alt="close" width={40} />
          </IconButton>

          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={position || { lat: 37.0902, lng: -95.7129 }}
            zoom={15}
            onLoad={handleMapLoad}
          >
            {position && (
              <>
                <Marker
                  position={position}
                  draggable
                  onDragEnd={(e) => {
                    const newPos = {
                      lat: e.latLng.lat(),
                      lng: e.latLng.lng(),
                    };
                    setPosition(newPos);
                    fetchAddress(newPos);
                  }}
                />
                <Circle center={position} radius={circularRadius} />
              </>
            )}
          </GoogleMap>
        </Box>
      )}

 <LocationPermissionModal
  open={showPermissionModal}
  onAllow={() => {
    setShowPermissionModal(false);
    handleGetCurrentLocation(); // ✅ Retry after user allows
  }}
  onCancel={() => {
    setShowPermissionModal(false);
    onPermission?.(false);
  }}
/>
    </Box>
  );
};

export default Location;